import React, { useEffect, useMemo, useState } from 'react';
import {
    FileText, FileSignature, ClipboardList, CalendarClock,
    AlertTriangle, Layers, PieChart, BookOpen, Users,
} from 'lucide-react';
import { govInsightsApi, GovernanceInsightsData } from '../../services/governanceService';
import Toast, { type ToastMessage } from '../common/Toast';
import InsightsSkeleton from '../common/InsightsSkeleton';
import NoDataFound from '../common/NoDataFound';
import { usePermission } from '../../contexts/PermissionContext';

const ACT_INSIGHTS = 'GOV_REPORTS';

const EMPTY: GovernanceInsightsData = {
    frameworks_pending: 0, sops_with_drafts: 0, meetings_upcoming: 0,
    tasks_open: 0, tasks_overdue: 0, tasks_due_week: 0,
    contracts_expiring_90: 0, contracts_expired: 0,
    frameworks_by_state: [], frameworks_by_type: [],
    contracts_by_status: [], contracts_by_type: [],
    sops_by_status: [], meetings_by_status: [],
};

const COLORS = ['#6366f1', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#f97316', '#8b5cf6', '#94a3b8'];

const STATE_COLOR: Record<string, string> = {
    APPROVED: '#10b981', DRAFT: '#6366f1', PENDING: '#f59e0b',
    REJECTED: '#ef4444', ACTIVE: '#10b981', INACTIVE: '#94a3b8',
    EXPIRED: '#ef4444', EXPIRING: '#f97316',
    IN_PROGRESS: '#06b6d4', CLOSED: '#94a3b8', CANCELLED: '#ef4444',
};

/* ── KPI Card ─────────────────────────────────────────────────────────── */
interface KpiCardProps {
    label: string; value: number; sub?: string;
    icon: React.ElementType; iconBg: string; iconColor: string;
}
const KpiCard = ({ label, value, sub, icon: Icon, iconBg, iconColor }: KpiCardProps) => (
    <div className="relative bg-white rounded-2xl flex items-center gap-4 px-5 py-5 cursor-default group overflow-hidden"
        style={{
            border: '1px solid #e8ecf4',
            boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)',
            transition: 'box-shadow 0.2s ease, transform 0.2s ease',
        }}
        onMouseEnter={e => {
            (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 28px rgba(0,0,0,0.11), 0 2px 6px rgba(0,0,0,0.06)';
            (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={e => {
            (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)';
            (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
        }}>
        {/* subtle bottom-right glow */}
        <div className="absolute bottom-0 right-0 w-24 h-24 rounded-full pointer-events-none"
            style={{ background: `radial-gradient(circle, ${iconColor}0d 0%, transparent 70%)`, transform: 'translate(30%, 30%)' }} />
        <div className="shrink-0 p-3 rounded-xl" style={{ background: iconBg, boxShadow: `0 2px 10px ${iconColor}28` }}>
            <Icon size={20} style={{ color: iconColor }} />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 truncate mb-1">{label}</p>
            <span className="text-[2rem] font-extrabold text-slate-800 leading-none tabular-nums">{value}</span>
            {sub && <p className="text-[10px] text-slate-400 mt-1 truncate">{sub}</p>}
        </div>
    </div>
);

/* ── Distribution Widget ──────────────────────────────────────────────── */
interface DistItem { label: string; count: number; color: string }
const DistributionWidget = ({ title, data, icon: Icon, total, accentColor = '#6366f1' }: {
    title: string; data: DistItem[]; icon: React.ElementType; total: number; accentColor?: string;
}) => {
    const maxCount = Math.max(...data.map(d => d.count), 1);
    return (
        <div className="rounded-2xl overflow-hidden flex flex-col"
            style={{
                background: '#ffffff',
                border: '1px solid #e8ecf4',
                boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)',
            }}>
            {/* header — white with bottom separator, accent used only for icon + badge */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
                <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg" style={{ background: `${accentColor}12` }}>
                        <Icon size={12} style={{ color: accentColor }} />
                    </div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{title}</span>
                </div>
                {total > 0 && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full tabular-nums"
                        style={{ background: `${accentColor}12`, color: accentColor }}>
                        {total}
                    </span>
                )}
            </div>

            {data.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8 italic">No data yet.</p>
            ) : (
                <ul className="flex-1 divide-y divide-slate-50">
                    {data.map((item, idx) => {
                        const barPct = item.count > 0 ? Math.round((item.count / maxCount) * 100) : 0;
                        const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                        return (
                            <li key={idx}
                                className="grid items-center gap-3 px-4 py-2.5 transition-colors duration-100"
                                style={{ gridTemplateColumns: '1fr 110px 3.5rem' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide truncate">
                                    {item.label}
                                </span>
                                {/* glossy bar track */}
                                <div className="h-1.5 rounded-full overflow-hidden"
                                    style={{ background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)' }}>
                                    <div className="h-1.5 rounded-full transition-all duration-500"
                                        style={{
                                            width: barPct > 0 ? `${barPct}%` : '0%',
                                            background: `linear-gradient(90deg, ${item.color}bb, ${item.color})`,
                                            boxShadow: barPct > 10 ? `0 1px 4px ${item.color}55` : 'none',
                                        }} />
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                    <span className="text-[12px] font-extrabold tabular-nums"
                                        style={{ color: item.count > 0 ? item.color : '#94a3b8' }}>
                                        {item.count}
                                    </span>
                                    <span className="text-[10px] text-slate-400">({pct}%)</span>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

/* ── Main Component ───────────────────────────────────────────────────── */
const GovernanceInsights: React.FC = () => {
    const [data, setData] = useState<GovernanceInsightsData>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const { allowed: canView, loading: viewLoading } = usePermission(ACT_INSIGHTS, 'view');

    useEffect(() => {
        if (!canView) return;
        let cancelled = false;
        setLoading(true);
        govInsightsApi.get()
            .then(d => { if (!cancelled && d) setData(d); })
            .catch(e => { if (!cancelled) setToast({ type: 'error', message: e?.response?.data?.detail || e?.message || 'Failed to load insights' }); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [canView]);

    const frameworkStateData = useMemo(() =>
        data.frameworks_by_state.map(r => ({ label: r.label, count: r.count, color: STATE_COLOR[r.label.toUpperCase()] || '#6366f1' })),
        [data.frameworks_by_state]);
    const frameworkTypeData = useMemo(() =>
        data.frameworks_by_type.map((r, i) => ({ label: r.label, count: r.count, color: COLORS[i % COLORS.length] })),
        [data.frameworks_by_type]);
    const contractStatusData = useMemo(() =>
        data.contracts_by_status.map(r => ({ label: r.label, count: r.count, color: STATE_COLOR[r.label.toUpperCase()] || '#6366f1' })),
        [data.contracts_by_status]);
    const contractTypeData = useMemo(() =>
        data.contracts_by_type.map((r, i) => ({ label: r.label, count: r.count, color: COLORS[i % COLORS.length] })),
        [data.contracts_by_type]);
    const sopStatusData = useMemo(() =>
        data.sops_by_status.map(r => ({ label: r.label, count: r.count, color: STATE_COLOR[r.label.toUpperCase()] || '#6366f1' })),
        [data.sops_by_status]);
    const meetingStatusData = useMemo(() =>
        data.meetings_by_status.map((r, i) => ({ label: r.label, count: r.count, color: STATE_COLOR[r.label.toUpperCase()] || COLORS[i % COLORS.length] })),
        [data.meetings_by_status]);

    const fwStateTotal   = useMemo(() => frameworkStateData.reduce((s, r) => s + r.count, 0), [frameworkStateData]);
    const fwTypeTotal    = useMemo(() => frameworkTypeData.reduce((s, r) => s + r.count, 0), [frameworkTypeData]);
    const ctrStatusTotal = useMemo(() => contractStatusData.reduce((s, r) => s + r.count, 0), [contractStatusData]);
    const ctrTypeTotal   = useMemo(() => contractTypeData.reduce((s, r) => s + r.count, 0), [contractTypeData]);
    const sopTotal       = useMemo(() => sopStatusData.reduce((s, r) => s + r.count, 0), [sopStatusData]);
    const meetingTotal   = useMemo(() => meetingStatusData.reduce((s, r) => s + r.count, 0), [meetingStatusData]);

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to governance insights." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full min-h-[500px]">
            <div className="netbody-content">
                <div className="scroll-container-nofilters flex-1 overflow-hidden">
                    {loading && <InsightsSkeleton kpiCount={4} breakdownCount={6} breakdownCols={3} />}

                    {!loading && (
                        <div className="py-4 space-y-4">
                            {/* KPI cards */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <KpiCard label="Frameworks Awaiting Approval" value={data.frameworks_pending}
                                    sub="Pending sign-off" icon={FileText} iconBg="#eff6ff" iconColor="#3b82f6" />
                                <KpiCard label="Agreements Expiring (90d)" value={data.contracts_expiring_90}
                                    sub={`${data.contracts_expired} already expired`} icon={FileSignature} iconBg="#fffbeb" iconColor="#f59e0b" />
                                <KpiCard label="SOPs with Pending Changes" value={data.sops_with_drafts}
                                    sub="Open drafts" icon={ClipboardList} iconBg="#f5f3ff" iconColor="#8b5cf6" />
                                <KpiCard label="Upcoming Meetings" value={data.meetings_upcoming}
                                    sub={`${data.tasks_overdue} tasks overdue`} icon={CalendarClock} iconBg="#ecfdf5" iconColor="#10b981" />
                            </div>

                            {data.tasks_overdue > 0 && (
                                <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-xl flex gap-2 items-center"
                                    style={{ boxShadow: '0 1px 4px rgba(239,68,68,0.08)' }}>
                                    <AlertTriangle size={13} className="text-red-500 shrink-0" />
                                    <p className="text-xs text-red-700">
                                        <span className="font-semibold">{data.tasks_overdue}</span> governance task{data.tasks_overdue === 1 ? '' : 's'} overdue
                                        {data.tasks_due_week > 0 && <> · <span className="font-semibold">{data.tasks_due_week}</span> due this week</>}
                                    </p>
                                </div>
                            )}

                            {/* Distribution widgets */}
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                <DistributionWidget title="Frameworks by Approval State" data={frameworkStateData} icon={Layers}        total={fwStateTotal}   accentColor="#6366f1" />
                                <DistributionWidget title="Frameworks by Type"           data={frameworkTypeData}  icon={BookOpen}      total={fwTypeTotal}    accentColor="#3b82f6" />
                                <DistributionWidget title="Agreements by Status"         data={contractStatusData} icon={FileSignature} total={ctrStatusTotal} accentColor="#f59e0b" />
                                <DistributionWidget title="Agreements by Type"           data={contractTypeData}   icon={PieChart}      total={ctrTypeTotal}   accentColor="#8b5cf6" />
                                <DistributionWidget title="SOPs by Status"               data={sopStatusData}      icon={ClipboardList} total={sopTotal}       accentColor="#10b981" />
                                <DistributionWidget title="Meetings by Status"           data={meetingStatusData}  icon={Users}         total={meetingTotal}   accentColor="#06b6d4" />
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default GovernanceInsights;
export { GovernanceInsights };
